import { useCallback, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useRecoilValue, useSetRecoilState } from 'recoil'

import { isSavingAtom, isSoleSelectionSelector } from '@/atoms/_editorAtoms'
import { editorRemintedItemIdsAtom } from '@/atoms/editorTextBoxAtoms'
import {
  editorTextBoxConvertSkipConfirmationAtom,
  showEditorTextBoxConvertModalForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { EditorTextBoxConvertIcon } from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import { useEditorConvertTextBox } from '@/hooks/editor/useEditorConvertTextBox'
import { useEditorLotusOffered } from '@/hooks/editor/useEditorLotusOffered'

type EditorTextBoxConvertControlsProps = {
  itemId: string
  itemsLocked: boolean
}

/**
 * The classic bar's control that turns a TinyMCE text item into a Lotus
 * text box, after a confirmation (`EditorTextBoxConvertModal`), or at once
 * once someone has stopped that confirmation in this browser
 * (`editorTextBoxConvertSkipConfirmationAtom`); converting at once, it is
 * held while another Save is writing the line, as the confirmation holds
 * Confirm. It shows to internal staff alone, where Lotus text boxes are
 * offered for the open order's funeral home (`useEditorLotusOffered`), on an item selected by itself
 * on a page whose items are not locked, and never on an item whose id the
 * load minted, which the stored page does not name yet. A box made this way
 * is an ordinary Lotus box for everyone.
 */
export const EditorTextBoxConvertControls = ({
  itemId,
  itemsLocked,
}: EditorTextBoxConvertControlsProps) => {
  const { t } = useTranslation()
  const offered = useEditorLotusOffered()
  const soleSelection = useRecoilValue(isSoleSelectionSelector(itemId))
  const remintedItemIds = useRecoilValue(editorRemintedItemIdsAtom)
  const skipConfirmation = useRecoilValue(
    editorTextBoxConvertSkipConfirmationAtom,
  )
  const isSaving = useRecoilValue(isSavingAtom)
  const confirmFor = useSetRecoilState(
    showEditorTextBoxConvertModalForItemIdAtom,
  )
  const convertTextBox = useEditorConvertTextBox()
  const held = skipConfirmation && isSaving

  const handleClick = useCallback(
    (event: MouseEvent) => {
      event.stopPropagation()
      if (!skipConfirmation) confirmFor(itemId)
      else if (!held) void convertTextBox(itemId)
    },
    [skipConfirmation, held, confirmFor, convertTextBox, itemId],
  )

  if (!offered || itemsLocked || !soleSelection || remintedItemIds[itemId])
    return null

  return (
    <Tooltip text={t('itemControls.convertToNewTextBox')}>
      <EditorTextBoxConvertIcon
        onClick={handleClick}
        icon="sparklesIcon"
        $held={held}
        aria-label={t('itemControls.convertToNewTextBox')}
        aria-disabled={held}
        data-testid="overlay-editor-text-box-convert-icon"
        data-overlay-action="editor-text-box-convert"
      />
    </Tooltip>
  )
}
